import React, { useCallback, useEffect, useState } from "react";
import Alert from "@mui/joy/Alert";
import Box from "@mui/joy/Box";
import IconButton from "@mui/joy/IconButton";
import Typography from "@mui/joy/Typography";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import BuildIcon from "@mui/icons-material/Build";
import { getApiBaseUrl } from "../config/runtimeConfig";

const POLL_INTERVAL_MS = 60_000;
const DISMISSED_KEY = "service_notice_dismissed";

interface ServiceNotice {
  enabled: boolean;
  message: string;
  severity: "info" | "warning" | "maintenance";
  updated_at?: string;
}

const severityConfig = {
  info: { color: "primary" as const, icon: <InfoOutlinedIcon /> },
  warning: { color: "warning" as const, icon: <WarningAmberIcon /> },
  maintenance: { color: "warning" as const, icon: <BuildIcon /> },
};

const ServiceNoticeBanner: React.FC = () => {
  const [notice, setNotice] = useState<ServiceNotice | null>(null);
  const [dismissed, setDismissed] = useState<string | null>(
    sessionStorage.getItem(DISMISSED_KEY),
  );

  const fetchNotice = useCallback(async () => {
    try {
      const res = await fetch(`${getApiBaseUrl()}/service-notice`);
      if (res.ok) {
        const data: ServiceNotice = await res.json();
        setNotice(data);
      }
    } catch {
      // Silently ignore -- banner is best-effort
    }
  }, []);

  useEffect(() => {
    fetchNotice();
    const interval = setInterval(fetchNotice, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchNotice]);

  if (!notice?.enabled || !notice.message) return null;

  // Allow re-show if the message changed since dismissal
  if (dismissed === notice.message) return null;

  const config = severityConfig[notice.severity] || severityConfig.info;

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 9999,
        width: "100%",
      }}
    >
      <Alert
        variant="soft"
        color={config.color}
        startDecorator={config.icon}
        endDecorator={
          <IconButton
            variant="plain"
            size="sm"
            color={config.color}
            onClick={() => {
              setDismissed(notice.message);
              sessionStorage.setItem(DISMISSED_KEY, notice.message);
            }}
          >
            <CloseIcon />
          </IconButton>
        }
        sx={{
          borderRadius: 0,
          justifyContent: "center",
          py: 1,
        }}
      >
        <Typography level="body-sm" sx={{ fontWeight: 500 }}>
          {notice.message}
        </Typography>
      </Alert>
    </Box>
  );
};

export default ServiceNoticeBanner;
